import { ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from "../../../../components/ui/common/sheet";
import { TwScope } from "../../../../components/ui/TwScope";

interface FacilityDrawerShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eyebrow: string;
  title: string;
  headerRight?: ReactNode;
  subtitle?: ReactNode;
  footer: ReactNode;
  children: ReactNode;
  /** Set false while a nested non-Radix modal (e.g. the semantic-ui Simulation modal) is open,
   * so this Sheet's focus trap and pointer-events lock release for it. */
  modal?: boolean;
}

const FacilityDrawerShell = ({
  open,
  onOpenChange,
  eyebrow,
  title,
  headerRight,
  subtitle,
  footer,
  children,
  modal = true
}: FacilityDrawerShellProps) => {
  if (!open) {
    return null;
  }

  return createPortal(
    <Sheet open={open} onOpenChange={onOpenChange} modal={modal}>
      <SheetContent
        side="right"
        className="tw-w-full sm:!tw-max-w-6xl !tw-p-0 tw-flex tw-flex-col tw-gap-0"
      >
        <TwScope className="tw-flex tw-flex-col tw-h-full tw-min-h-0">
          <SheetHeader className="tw-flex-row tw-items-start tw-justify-between tw-space-y-0 tw-text-left tw-gap-3 tw-px-6 tw-py-4 tw-border-b tw-border-border tw-shrink-0">
            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
              <p className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium">
                {eyebrow}
              </p>
              <SheetTitle className="tw-text-lg tw-font-bold tw-text-foreground tw-truncate">
                {title}
              </SheetTitle>
              {subtitle}
            </div>
            {headerRight && <div className="tw-shrink-0">{headerRight}</div>}
          </SheetHeader>

          <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden tw-scrollbar-none [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:tw-none] [scrollbar-width:tw-none]">
            <div className="tw-p-6 tw-space-y-6 tw-bg-muted/20 tw-min-h-full">{children}</div>
          </div>
          <div className="tw-shrink-0 tw-border-t tw-border-border tw-bg-background">{footer}</div>
        </TwScope>
      </SheetContent>
    </Sheet>,
    document.body
  );
};

export default FacilityDrawerShell;
